import {
  format,
  parse,
  parseISO,
  isAfter,
  isBefore,
  isSameDay,
  addDays,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addMonths,
  differenceInMinutes,
} from "date-fns";

// Combine appointment date (YYYY-MM-DD) + time (HH:mm) into local Date
export const apptToDate = (appt) => {
  if (!appt?.date) return null;
  const dateStr = `${appt.date}T${appt.time || "00:00"}:00`;
  return parse(dateStr, "yyyy-MM-dd'T'HH:mm:ss", new Date());
};

export const formatDateLong = (d) => format(d, "EEEE, d MMMM yyyy");
export const formatDateShort = (d) => format(d, "EEE, d MMM");
export const formatTimePretty = (d) => format(d, "h:mm a");
export const formatMonthYear = (d) => format(d, "MMMM yyyy");

// Bucket appointments into sections: This Week + each remaining month of current year
export const bucketAppointments = (appointments, now = new Date()) => {
  const today = startOfDay(now);
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

  const withDates = appointments
    .map((a) => ({ ...a, _dt: apptToDate(a) }))
    .filter((a) => a._dt instanceof Date && !isNaN(a._dt));

  // Future or today's appointments
  const future = withDates
    .filter((a) => isAfter(a._dt, addDays(now, -0.001)) || isSameDay(a._dt, now))
    .sort((a, b) => a._dt - b._dt);

  const thisWeek = future.filter(
    (a) => (isAfter(a._dt, weekStart) || isSameDay(a._dt, weekStart)) && isBefore(a._dt, endOfDay(weekEnd))
  );

  // Build monthly buckets from current month through December of current year
  const currentYear = now.getFullYear();
  const currentMonthIdx = now.getMonth(); // 0-11
  const months = [];
  for (let m = currentMonthIdx; m <= 11; m += 1) {
    const monthDate = new Date(currentYear, m, 1);
    const mStart = startOfMonth(monthDate);
    const mEnd = endOfMonth(monthDate);
    const items = future.filter(
      (a) => (isAfter(a._dt, mStart) || isSameDay(a._dt, mStart)) && isBefore(a._dt, endOfDay(mEnd))
    );
    months.push({
      key: `${currentYear}-${m + 1}`,
      monthDate,
      items,
    });
  }

  return { thisWeek, months, all: future, today };
};

// Reminder logic: returns array of active reminders for an appointment
export const getReminders = (appt, now = new Date()) => {
  const dt = apptToDate(appt);
  if (!dt) return [];
  const diffMin = differenceInMinutes(dt, now);
  const reminders = [];
  // 1 day before window: 1440 min (24h) — active when within 24h to 3h+1
  if (diffMin > 180 && diffMin <= 24 * 60) {
    reminders.push({ key: "1day", label: "1 day reminder" });
  }
  // 3 hours before window: active when within 3h until appointment starts
  if (diffMin > 0 && diffMin <= 180) {
    reminders.push({ key: "3hr", label: "3 hour reminder" });
  }
  // Happening now / today
  if (diffMin <= 0 && diffMin > -120) {
    reminders.push({ key: "now", label: "Happening now" });
  }
  return reminders;
};

export {
  format,
  parseISO,
  addDays,
  startOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  isSameDay,
};
